import {
  describeApiError,
  type Leaderboard,
  type LeaderboardStatus,
  type PointsSystem,
  type RecalcMode,
} from '@brush/api-client';
import {
  DEAL_CHOICES,
  ErrorNotice,
  PINNED_EXPLANATION,
  Pill,
  RECALC_CHOICES,
  REENTRY_CHOICES,
  RUNNING_SCOPE_WARNING,
  START_NEEDS_SYSTEM,
  Spinner,
  latestPublished,
  readDefinition,
  scopeKey,
  scopeSummary,
  tagVocabularyFromUsage,
  toScopeDocument,
  useDebouncedValue,
  validateScope,
  type PointsScope,
} from '@brush/ui';
import { AlertTriangle, ChevronDown, ChevronRight, Lock, RefreshCw } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  useCloseLeaderboard,
  useLeaderboardDrift,
  usePlatformTags,
  usePointsSystemVersion,
  usePointsSystems,
  useRecalculateLeaderboard,
  useScopeCandidates,
  useScopeEvents,
  useSetLeaderboardRecalculation,
  useStartLeaderboard,
  useTournamentTagUsage,
} from '../../queries';
import { modalControl, modalInput } from '../Modal';
import { candidateParams } from '../../leaderboards/candidates';
import { candidateList } from '../../leaderboards/candidateList';
import { LeaderboardScopeFilters } from './LeaderboardScopeFilters';

/**
 * §9.9 — leaderboard setup. **A section of the existing leaderboard edit screen, not a page.**
 *
 * Four things live here, and they answer four different questions:
 *
 * 1. **Which events this leaderboard scores** — §4.2's scope, with a live count from the server.
 *    This is the section that was previously, wrongly, in the points system builder: a system
 *    answers only how a finish becomes points, and dates belong to the board using it (§3.2).
 * 2. **Which points system** — published ones only, own plus platform, and after the board starts
 *    the pinned version and nothing else.
 * 3. **What recalculates it** — the one control §9.9 leaves editable while a board runs.
 * 4. **Recalculate now**, with §8.1's drift indicator beside it.
 *
 * <b>The status table in §9.9 is enforced here as *shape*, not as `disabled`.</b> A running board
 * does not show a greyed-out system picker; it shows the pinned version and the sentence saying
 * why that is permanent. A greyed control says "not now" where the truth is "never, and here is
 * the reason" — and §9.9 says so in as many words: <i>do not disable the control silently</i>.
 *
 * <b>Scope saves with the form; everything else acts at once.</b> Scope is a field of the
 * leaderboard, so it rides on the same Save as the name and the dates. Starting, closing and
 * recalculating are not fields — each is a transition with its own consequences, and each is its
 * own button and its own request. The one place the two meet is Start, which is refused while the
 * scope is unsaved: pinning a version against a scope the server has not got would take the pin
 * on one board and score another.
 */
export function LeaderboardPointsSection({
  leaderboard,
  roomId,
  scope,
  onScopeChange,
  dateStart,
  dateEnd,
  isAdmin,
  scopeDirty,
  systemId,
  versionNumber,
  onSelectSystem,
  systemDirty,
}: {
  /** Null while the leaderboard is being created — it has no id, so nothing lifecycle works yet. */
  leaderboard: Leaderboard | null;
  roomId: string;
  /** The draft's scope. Null means "the window this board already declares" — see `effectiveScope`. */
  scope: PointsScope | null;
  onScopeChange: (next: PointsScope | null) => void;
  /** The form's live dates, which is what a null scope falls back to — server side and here. */
  dateStart: string;
  dateEnd: string;
  isAdmin: boolean;
  /** Whether the scope on screen differs from the one the server holds. Gates Start. */
  scopeDirty: boolean;
  /**
   * The chosen points system, held by the builder because it **saves with the form**.
   *
   * It is a field of the leaderboard now, not an argument to Start: a draft carries the selection
   * so a board can be set up in one pass. Start still pins it, and from that moment the server
   * refuses any change (§2.3).
   */
  systemId: string;
  /** Which version of it, or null for the newest published. */
  versionNumber: number | null;
  onSelectSystem: (systemId: string, versionNumber: number | null) => void;
  /** Whether the selection on screen differs from the stored one. Gates Start, like `scopeDirty`. */
  systemDirty: boolean;
}) {
  const status: LeaderboardStatus = (leaderboard?.status as LeaderboardStatus | undefined) ?? 'draft';
  const locked = status === 'closed';

  return (
    <div className="space-y-6">
      <ScopeBlock
        roomId={roomId}
        scope={scope}
        onScopeChange={onScopeChange}
        dateStart={dateStart}
        dateEnd={dateEnd}
        status={status}
      />

      <PointsSystemBlock
        leaderboard={leaderboard}
        roomId={roomId}
        status={status}
        scopeDirty={scopeDirty}
        systemId={systemId}
        versionNumber={versionNumber}
        onSelectSystem={onSelectSystem}
        systemDirty={systemDirty}
      />

      {/* Not on a draft: there the trigger is part of Start, since it rides on the same request
          that takes the pin. Two radio groups asking the same question on one screen is a screen
          where changing the wrong one silently does nothing. */}
      {leaderboard && status !== 'draft' ? (
        <>
          <RecalculationBlock leaderboard={leaderboard} roomId={roomId} locked={locked} />
          <RecalculateNowBlock leaderboard={leaderboard} roomId={roomId} isAdmin={isAdmin} />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- 1. which events

/**
 * §9.9's first field, and the one an operator spends the most time in.
 *
 * <b>The filters are always on screen.</b> They were behind a disclosure once, and that was a
 * mistake worth recording: a screen whose entire job is choosing which events count opened showing
 * a sentence and a text link, so the controls that do the choosing — tags, minimum field size, the
 * per-event overrides — were invisible in the default state, which is every leaderboard's. Worse,
 * the fantasy tournament picker further down the dialog looked like the way to pick events. It
 * never was; it has since been removed, and these are unhidden.
 *
 * <b>"Not set" is still a real answer and still the default.</b> A leaderboard already declares a
 * start and an end date, and a board scoring everything in that window needs no scope document —
 * which is exactly what the server does with a null one (`LeaderboardRecalculator.ScopeOf`). So the
 * editor is *displayed* against `effectiveScope`, which falls back to those dates and keeps
 * tracking them, and a scope is only materialised when the operator actually changes something.
 * Seeding a document on first render instead would make every board carry a scope that repeats its
 * own date fields and then silently stop following them the moment somebody moved a date.
 *
 * The live count is asked for either way, because the fallback resolves to a number too and "how
 * many events is this?" is the question the operator has whether or not they have narrowed it.
 */
function ScopeBlock({
  roomId,
  scope,
  onScopeChange,
  dateStart,
  dateEnd,
  status,
}: {
  roomId: string;
  scope: PointsScope | null;
  onScopeChange: (next: PointsScope | null) => void;
  dateStart: string;
  dateEnd: string;
  status: LeaderboardStatus;
}) {
  const platformTags = usePlatformTags();

  const readOnly = status === 'closed';

  /**
   * What the server will actually resolve.
   *
   * <b>The dates always come from the leaderboard's own Start and End, never from the scope
   * document.</b> They were two independent ranges once, and that was a silent trap rather than a
   * feature: a scope only affects scoring when it exists, so setting a tag filter would freeze the
   * scoring window at whatever the dates happened to be, and a later edit to the board's Start date
   * would move the header and the "Active" chip while the standings kept scoring the old range —
   * with nothing on screen disagreeing.
   *
   * There was never a case that needed two. `DateStart`–`DateEnd` is the fallback the server uses
   * when no scope is stored (`LeaderboardRecalculator.ScopeOf`), so with no scope they are the same
   * range *by construction*; the schema requires `startDate`/`endDate` on the document, so they are
   * written from the board's fields and kept in step. An event outside the window that must still
   * count has its own answer, and a better one — "Always scores" overrides the dates (§3.2).
   */
  const effectiveScope = useMemo<PointsScope>(
    () => ({
      ...(scope ?? fallbackScope(dateStart, dateEnd)),
      startDate: dateStart,
      endDate: dateEnd,

      // No control for it here, so it is pinned rather than carried invisibly. A stored value
      // nothing can see or change is a filter quietly removing events from a season, and it is
      // also the one §3.2 rule the browser cannot evaluate — with it at 0 the candidate list on
      // screen and the set the server scores are the same by construction. §3.2 still has the
      // rule and the designer's test scope still offers it.
      minFieldSize: 0,
    }),
    [scope, dateStart, dateEnd],
  );

  const validation = useMemo(() => validateScope(effectiveScope), [effectiveScope]);

  // Debounced together, so dragging a date and then ticking a tag costs one request rather than
  // two — the endpoint shares the preview's per-caller rate limit.
  const debounced = useDebouncedValue(effectiveScope, 800);

  const events = useScopeEvents({
    roomId,
    scope: toScopeDocument(debounced),
    enabled: validation.isValid,
  });

  // The tick list follows the same debounced scope as the count, read page after page (known gap 35).
  const candidates = candidateList(
    useScopeCandidates(roomId, candidateParams(debounced)),
    effectiveScope,
    debounced,
  );

  // Every tag the room's tournaments carry, from the room's tag counts rather than a fetched list (known gap 35).
  const tagUsage = useTournamentTagUsage(roomId);
  const tags = useMemo(
    () => tagVocabularyFromUsage(tagUsage.data ?? [], platformTags.data ?? []),
    [tagUsage.data, platformTags.data],
  );

  /**
   * Whether the count on screen is a count *of what is on screen*.
   *
   * The request lags the controls by the debounce, so between a keystroke and the answer there is
   * a window where the last count is still in hand — and printing it beside the new date range
   * would state a number about a scope the operator has already changed. That is the same mistake
   * §9.8's comparison avoids by withholding movement while either side is fetching, and it is
   * worse here: the count is the whole feedback loop, so a wrong one sends somebody off to fix a
   * filter that was doing the right thing. Null instead, and the header says "Counting…".
   */
  const settled = scopeKey(debounced) === scopeKey(effectiveScope) && !events.isFetching;
  const count = settled ? events.data?.eventsInScope ?? null : null;

  return (
    <section className="min-w-0">
      <Header
        title="Which events this leaderboard scores"
        summary={scopeSummary(effectiveScope, count)}
        right={
          !settled ? (
            <span className="text-[11px] text-ink-800/45">Counting…</span>
          ) : events.isError ? (
            // The query never retries and is cached per scope, so without this the count stays gone until the
            // scope changes — a rate-limit refusal outlasts its own minute (#222). Why is said below the filters.
            <span className="text-[11px] text-chip-500">
              Count unavailable ·{' '}
              <button type="button" onClick={() => void events.refetch()} className="underline underline-offset-2">
                Try again
              </button>
            </span>
          ) : count !== null ? (
            <span className="num text-xs font-semibold text-ink-950">
              {count} event{count === 1 ? '' : 's'}
            </span>
          ) : null
        }
      />

      <div className="space-y-3">
        {status === 'running' ? (
          <p className="flex gap-2 border border-gold-500/40 bg-gold-500/5 p-2.5 text-[11.5px] leading-relaxed text-ink-900">
            <AlertTriangle className="mt-px size-3.5 shrink-0 text-gold-500" />
            <span>{RUNNING_SCOPE_WARNING}</span>
          </p>
        ) : null}

        <p className="text-[11.5px] leading-relaxed text-ink-800/60">
          Between <strong>{dateStart}</strong> and <strong>{dateEnd}</strong> — the leaderboard’s
          own dates, above.{' '}
          {scope === null ? (
            <>
              <strong>Every event in that range counts.</strong> Narrow it with the filters below.
            </>
          ) : (
            <>Narrowed by the filters below.</>
          )}
        </p>

        {/* Filter, then tick. Not the shared `ScopeEditor`: that presents §4.2's document field
            by field, which suits the designer's test scope, where the subject is the rule. Here the
            subject is the season — see LeaderboardScopeFilters for the mapping back to the
            document, and for why the tag quantifier differs from the tournaments list. */}
        <LeaderboardScopeFilters
          scope={effectiveScope}
          onChange={onScopeChange}
          candidates={candidates}
          tags={tags}
          readOnly={readOnly}
          subject="leaderboard"
        />

        {/* Clearing is a real state, not a reset: null means "score the whole window", which is
            what the server does with no scope stored. Offered only once there is something to
            clear. */}
        {scope !== null && !readOnly ? (
          <button
            type="button"
            onClick={() => onScopeChange(null)}
            className="text-[11px] text-ink-800/55 underline underline-offset-2 hover:text-ink-950"
          >
            Clear these filters and score everything between the leaderboard’s dates
          </button>
        ) : null}
      </div>

      {/* §9.7's empty state. The filter list above already shows *that* nothing matched and lets
          an operator see why; what it cannot show is the case where candidates exist on screen and
          the server still scores none — which now means every one of them was unticked. */}
      {count === 0 ? (
        <p className="mt-3 border border-chip-500/30 bg-chip-500/5 p-2.5 text-[11.5px] leading-relaxed text-ink-900">
          <strong>No events are in scope</strong>, so this leaderboard would rank nobody. Tick at
          least one tournament above, or widen the dates.
        </p>
      ) : null}

      <ErrorNotice message={describeApiError(events.error)} className="mt-3" />
    </section>
  );
}

/** Everything in the board's declared window and nothing else — the server's own fallback (§3.2). */
function fallbackScope(startDate: string, endDate: string): PointsScope {
  return {
    startDate,
    endDate,
    includeTags: [],
    excludeTags: [],
    includeEventIds: [],
    excludeEventIds: [],
    minFieldSize: 0,
  };
}

// ---------------------------------------------------------------- 2. the points system

/**
 * §9.9's picker while a board is a Draft, and §2.3's pin once it is not.
 *
 * The two are different components rather than one with a `disabled` flag, because they say
 * different things. Before the start there is a choice to make and a version to default; after it
 * there is a fact, and the useful thing to show is what was pinned and why it cannot move.
 */
function PointsSystemBlock({
  leaderboard,
  roomId,
  status,
  scopeDirty,
  systemId,
  versionNumber,
  onSelectSystem,
  systemDirty,
}: {
  leaderboard: Leaderboard | null;
  roomId: string;
  status: LeaderboardStatus;
  scopeDirty: boolean;
  systemId: string;
  versionNumber: number | null;
  onSelectSystem: (systemId: string, versionNumber: number | null) => void;
  systemDirty: boolean;
}) {
  // Archived systems are left out: archiving is what hides a system from pickers (§2.4), and a
  // leaderboard already pinned to one keeps scoring regardless.
  const systems = usePointsSystems(roomId, false);

  if (status !== 'draft') {
    return <PinnedSystem leaderboard={leaderboard} systems={systems.data ?? []} />;
  }

  return (
    <StartLeaderboard
      leaderboard={leaderboard}
      roomId={roomId}
      systems={systems.data ?? []}
      loading={systems.isLoading}
      scopeDirty={scopeDirty}
      systemId={systemId}
      versionNumber={versionNumber}
      onSelectSystem={onSelectSystem}
      systemDirty={systemDirty}
    />
  );
}

/**
 * §2.3, made visible. The version, the system it belongs to, and the sentence §9.9 dictates.
 *
 * The system may since have been archived, or edited into three further versions; neither changes
 * what this board scores with, which is the whole point. Where the catalogue can still name it we
 * show the name, and where it cannot we still show the version number — the pin is on the
 * leaderboard row itself and is never in doubt.
 */
function PinnedSystem({
  leaderboard,
  systems,
}: {
  leaderboard: Leaderboard | null;
  systems: PointsSystem[];
}) {
  const system = systems.find((s) => s.id === leaderboard?.pointsSystemId) ?? null;

  return (
    <section className="min-w-0">
      <Header
        title="Points system"
        summary="What a finish is worth on this leaderboard."
        right={
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-800/55">
            <Lock className="size-3" /> Locked
          </span>
        }
      />

      <div className="rounded-lg border border-ink-800/15 bg-felt-50/30 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <span className="font-medium text-ink-950">
            {system?.name ?? 'A points system this room can no longer see'}
          </span>
          <span className="num text-xs text-ink-800/60">
            Version {leaderboard?.pointsSystemVersionNumber ?? '—'}
          </span>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {system?.isPlatform ? <Pill>Platform</Pill> : null}
          {system?.isArchived ? <Pill tone="warn">Archived</Pill> : null}
        </div>

        <p className="mt-2 text-[11.5px] leading-relaxed text-ink-800/65">{PINNED_EXPLANATION}</p>
      </div>

      {leaderboard?.pointsSystemId && leaderboard.pointsSystemVersionNumber ? (
        <DefinitionSummary
          systemId={leaderboard.pointsSystemId}
          versionNumber={leaderboard.pointsSystemVersionNumber}
        />
      ) : null}
    </section>
  );
}

/**
 * §9.9's picker, and the transition it feeds.
 *
 * <b>Drafts never appear.</b> A leaderboard pins a version so that what computes cannot change
 * underneath it, and a draft is by definition still changing — the server refuses one, and
 * offering it here would be a control that exists to be rejected. A system with nothing published
 * is therefore listed as unavailable rather than hidden: "where did my system go" is a worse
 * question than "why can't I pick this one", and the answer to the second is on the row.
 */
function StartLeaderboard({
  leaderboard,
  roomId,
  systems,
  loading,
  scopeDirty,
  systemId,
  versionNumber,
  onSelectSystem,
  systemDirty,
}: {
  leaderboard: Leaderboard | null;
  roomId: string;
  systems: PointsSystem[];
  loading: boolean;
  scopeDirty: boolean;
  systemId: string;
  versionNumber: number | null;
  onSelectSystem: (systemId: string, versionNumber: number | null) => void;
  systemDirty: boolean;
}) {
  const start = useStartLeaderboard(roomId);

  /*
    The recalculation trigger stays local, unlike the system above it. It is not a field of the
    leaderboard on a draft — it rides on the Start request itself, and once the board is running it
    has a block and a route of its own (`RecalculationBlock`). Lifting it into the form would make
    Save appear to set something Save does not send.
  */
  const [recalcMode, setRecalcMode] = useState<RecalcMode>('manual');
  const [recalcCron, setRecalcCron] = useState('0 3 * * *');

  const chosen = systems.find((s) => s.id === systemId) ?? null;
  const published = useMemo(
    () =>
      (chosen?.versions ?? [])
        .filter((v) => v.status === 'published')
        .sort((a, b) => b.versionNumber - a.versionNumber),
    [chosen],
  );

  // Defaults to the newest published version — what a room adopting the system today would be
  // scored by — and an operator may pin an older one deliberately.
  const version = versionNumber ?? published[0]?.versionNumber ?? null;

  /*
    Start acts on the board **as saved**, never on what is merely on screen — so every unsaved
    change is a block rather than something the request carries. The system is a saved field now,
    so the request names none and the server pins whatever the board holds; that is what makes it
    impossible to pin one version while the screen shows another.
  */
  const blocked =
    !leaderboard
      ? 'Save the leaderboard first — the system you pick here is saved with it. Starting is what pins that version for good, and there is nothing to pin to yet.'
      : systemId === ''
        ? START_NEEDS_SYSTEM
        : version === null
          ? 'That system has no published version to pin. Publish one from Settings → Points Systems first — a leaderboard can’t score against a definition that is still being edited.'
          : systemDirty
            ? 'Save the leaderboard first. The points system on screen has not been saved, and starting pins the one the server holds.'
            : scopeDirty
              ? 'Save the leaderboard first. The events it scores have changed on screen but not on the server, and starting would pin a version against the old set.'
              : null;

  return (
    <section className="min-w-0">
      <Header
        title="Points system"
        summary="What a finish is worth. Picked once — starting the leaderboard pins the version for good."
      />

      {loading ? (
        <Spinner label="Loading points systems…" />
      ) : systems.length === 0 ? (
        <p className="text-xs text-ink-800/60">
          This room has no points systems yet. Build one under{' '}
          <strong>Settings → Points Systems</strong>, publish it, and it will appear here.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <label className="block min-w-0">
              <span className="mb-1 block text-xs text-ink-800/60">System</span>
              <select
                value={systemId}
                onChange={(e) => onSelectSystem(e.target.value, null)}
                className={modalInput}
              >
                <option value="">Choose a points system…</option>
                {systems.map((system) => {
                  const newest = latestPublished(system);
                  return (
                    <option key={system.id} value={system.id} disabled={!newest}>
                      {system.name}
                      {system.isPlatform ? ' (platform)' : ''}
                      {newest ? '' : ' — nothing published yet'}
                    </option>
                  );
                })}
              </select>
            </label>

            <label className="block min-w-0">
              <span className="mb-1 block text-xs text-ink-800/60">Version</span>
              <select
                value={version ?? ''}
                disabled={published.length === 0}
                onChange={(e) => onSelectSystem(systemId, Number.parseInt(e.target.value, 10))}
                className={modalInput}
              >
                {published.length === 0 ? <option value="">—</option> : null}
                {published.map((v, index) => (
                  <option key={v.versionNumber} value={v.versionNumber}>
                    Version {v.versionNumber}
                    {index === 0 ? ' (latest)' : ''}
                    {v.publishedAt ? ` · ${v.publishedAt.slice(0, 10)}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {chosen && version !== null ? (
            <DefinitionSummary systemId={chosen.id} versionNumber={version} />
          ) : null}

          <div>
            <span className="mb-1.5 block text-xs text-ink-800/60">Recalculate</span>
            <RecalcChoices
              name="start-recalc-mode"
              value={recalcMode}
              onChange={setRecalcMode}
              cron={recalcCron}
              onCronChange={setRecalcCron}
            />
          </div>

          {blocked ? (
            <p className="text-[11.5px] leading-relaxed text-ink-800/60">{blocked}</p>
          ) : null}

          <button
            type="button"
            disabled={blocked !== null || start.isPending}
            onClick={() =>
              leaderboard &&
              start.mutate({
                id: leaderboard.id,
                body: {
                  // Null: pin what the board has stored, not what this form is showing. They are
                  // equal — `systemDirty` blocks the button otherwise — and sending null is what
                  // keeps that true by construction rather than by the gate being right.
                  pointsSystemId: null,
                  version: null,
                  recalcMode,
                  recalcCron: recalcMode === 'scheduled' ? recalcCron.trim() : null,
                },
              })
            }
            className="rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-40"
          >
            {start.isPending ? 'Starting…' : 'Start leaderboard'}
          </button>

          <ErrorNotice message={describeApiError(start.error)} />
        </div>
      )}
    </section>
  );
}

/**
 * §9.9's read-only summary of the chosen system: scoring type, deal rule, aggregation.
 *
 * §9.9 also asks for a "View definition" link opening the builder read-only in a new tab. Neither
 * console routes by URL — `/points-systems` is a screen, not a route — so there is no address to
 * open, and inventing routing for one link would cut across room-web's central unsaved-work guard,
 * which every exit from this dialog goes through. The summary is the part that answers the question
 * an operator actually has here; the definition itself is one screen away under Settings → Points
 * Systems.
 */
function DefinitionSummary({
  systemId,
  versionNumber,
}: {
  systemId: string;
  versionNumber: number;
}) {
  const version = usePointsSystemVersion(systemId, versionNumber);

  if (version.isLoading) return <p className="text-[11px] text-ink-800/45">Reading the definition…</p>;
  if (!version.data) return null;

  const definition = readDefinition(version.data.definition);
  const deal = DEAL_CHOICES.find((c) => c.value === definition.dealHandling);
  const reentry = REENTRY_CHOICES.find((c) => c.value === definition.reentryPolicy);

  const facts: [string, string][] = [
    [
      'Scoring',
      definition.scoring.type === 'table'
        ? `Placement table, ${definition.scoring.rows.length} rows`
        : 'Formula',
    ],
    ['Chops', deal?.label ?? definition.dealHandling],
    [
      'Totals',
      definition.aggregation.mode === 'bestN'
        ? `Best ${definition.aggregation.n} events`
        : 'Every event added up',
    ],
    ['Re-entries', reentry?.label ?? definition.reentryPolicy],
  ];

  return (
    <dl className="grid gap-x-4 gap-y-1 rounded-lg border border-ink-800/10 bg-white p-3 text-[11.5px] sm:grid-cols-2">
      {facts.map(([label, value]) => (
        <div key={label} className="flex min-w-0 gap-2">
          <dt className="shrink-0 text-ink-800/50">{label}</dt>
          <dd className="min-w-0 truncate text-ink-950" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------- 3. recalculation

/**
 * The one field §9.9 leaves editable on a Running board — and locks on a closed one for
 * administrators too. §8.2's override is a deliberate act with a reason attached; a schedule
 * quietly rewriting standings that determine prizes is the opposite of one.
 *
 * Saved on change rather than with the form, because it is not a field of the leaderboard resource
 * — it has its own route and its own refusals, and a cron that does not parse is a 400 the operator
 * should see against the box they typed it into.
 */
function RecalculationBlock({
  leaderboard,
  roomId,
  locked,
}: {
  leaderboard: Leaderboard;
  roomId: string;
  locked: boolean;
}) {
  const save = useSetLeaderboardRecalculation(roomId);
  const mode = leaderboard.recalcMode as RecalcMode;
  const [cron, setCron] = useState(leaderboard.recalcCron ?? '0 3 * * *');

  if (locked) {
    return (
      <section className="min-w-0">
        <Header
          title="Recalculation"
          summary={RECALC_CHOICES.find((c) => c.value === mode)?.label ?? mode}
          right={
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-800/55">
              <Lock className="size-3" /> Locked
            </span>
          }
        />
        <p className="text-[11.5px] leading-relaxed text-ink-800/65">
          This leaderboard is closed, so nothing recalculates it automatically any more. An
          administrator can still recalculate it once, with a reason — that is the only way
          standings move after a close.
        </p>
      </section>
    );
  }

  return (
    <section className="min-w-0">
      <Header
        title="Recalculation"
        summary="What updates the standings without anybody pressing anything."
        right={save.isPending ? <span className="text-[11px] text-ink-800/45">Saving…</span> : null}
      />

      <RecalcChoices
        name="leaderboard-recalc-mode"
        value={mode}
        onChange={(next) =>
          save.mutate({
            id: leaderboard.id,
            recalcMode: next,
            recalcCron: next === 'scheduled' ? cron.trim() : null,
          })
        }
        cron={cron}
        onCronChange={setCron}
        onCronCommit={() =>
          mode === 'scheduled' &&
          save.mutate({ id: leaderboard.id, recalcMode: 'scheduled', recalcCron: cron.trim() })
        }
      />

      <ErrorNotice message={describeApiError(save.error)} className="mt-2" />
    </section>
  );
}

/**
 * §9.9's radio, plus the schedule box when it is wanted.
 *
 * The cron is committed on blur rather than per keystroke: it is parsed on write, so every
 * intermediate state of "0 3 * * *" is a 400, and a form that goes red four times while somebody
 * types a valid expression teaches them to stop reading it.
 */
function RecalcChoices({
  name,
  value,
  onChange,
  cron,
  onCronChange,
  onCronCommit,
}: {
  /** The radio group's name. Distinct per call site — two groups sharing one are one group. */
  name: string;
  value: RecalcMode;
  onChange: (next: RecalcMode) => void;
  cron: string;
  onCronChange: (next: string) => void;
  onCronCommit?: () => void;
}) {
  return (
    <div className="space-y-2">
      {RECALC_CHOICES.map((choice) => {
        const active = choice.value === value;
        return (
          <label
            key={choice.value}
            className={`flex cursor-pointer gap-2.5 rounded-lg border p-2.5 transition ${
              active ? 'border-felt-700 bg-felt-50/60' : 'border-ink-800/15 hover:border-ink-800/30'
            }`}
          >
            <input
              type="radio"
              name={name}
              checked={active}
              onChange={() => onChange(choice.value)}
              className="mt-0.5 accent-[var(--color-felt-700)]"
            />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-ink-950">{choice.label}</span>
              <span className="mt-0.5 block text-[11px] leading-relaxed text-ink-800/60">
                {choice.detail}
              </span>
              {active && choice.value === 'scheduled' ? (
                <span className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    value={cron}
                    onChange={(e) => onCronChange(e.target.value)}
                    onBlur={onCronCommit}
                    aria-label="Schedule"
                    spellCheck={false}
                    className={`num w-40 ${modalControl}`}
                  />
                  <span className="text-[10.5px] text-ink-800/50">
                    Five fields, in UTC. <span className="num">0 3 * * *</span> is 3am every day.
                  </span>
                </span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- 4. recalculate now, and drift

/**
 * §9.9's Recalculate button, with §8.1's drift indicator beside it.
 *
 * <b>Drift is asked for only by an administrator.</b> The endpoint is admin-only, and §8.1 asks
 * for it as an admin alert; firing it as an operator would put a permanent 403 notice on the
 * screen, which reads as a broken app rather than as a report they don't have access to.
 *
 * <b>Recalculating a closed board needs a reason, and the box appears before the attempt.</b> The
 * server requires it (§8.2) and would refuse without it, but discovering that by being refused
 * makes the audit trail feel like an obstacle. It is the point of the operation.
 */
function RecalculateNowBlock({
  leaderboard,
  roomId,
  isAdmin,
}: {
  leaderboard: Leaderboard;
  roomId: string;
  isAdmin: boolean;
}) {
  const closed = leaderboard.status === 'closed';

  const recalculate = useRecalculateLeaderboard(roomId);
  const close = useCloseLeaderboard(roomId);
  const drift = useLeaderboardDrift(leaderboard.id, isAdmin);

  const [reason, setReason] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);

  const canRecalculate = !closed || (isAdmin && reason.trim().length > 0);

  return (
    <section className="min-w-0">
      <Header
        title="Standings"
        summary={
          closed
            ? 'Closed. The final snapshot is what determines prizes.'
            : 'Running. Recalculating writes a snapshot of the board as it stands.'
        }
      />

      {closed && isAdmin ? (
        <label className="mb-3 block">
          <span className="mb-1 block text-xs text-ink-800/60">
            Why are you recalculating a closed leaderboard?
          </span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Event 3 ended in a heads-up chop; the deal flag was missed on import"
            className={modalInput}
          />
          <span className="mt-1 block text-[10.5px] text-ink-800/45">
            Recorded on the snapshot. Prizes may already have been paid against the standings this
            replaces, and the previous final snapshot is kept.
          </span>
        </label>
      ) : null}

      {closed && !isAdmin ? (
        <p className="mb-3 text-[11.5px] leading-relaxed text-ink-800/65">
          Only a platform administrator can recalculate a closed leaderboard, and only with a
          reason.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!canRecalculate || recalculate.isPending}
          onClick={() =>
            recalculate.mutate(
              { id: leaderboard.id, reason: closed ? reason.trim() : null },
              { onSuccess: () => setReason('') },
            )
          }
          className="inline-flex items-center gap-2 rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-800 hover:bg-felt-50 disabled:opacity-40"
        >
          <RefreshCw className={`size-4 ${recalculate.isPending ? 'animate-spin' : ''}`} />
          {recalculate.isPending ? 'Recalculating…' : 'Recalculate now'}
        </button>

        {!closed ? (
          <button
            type="button"
            disabled={close.isPending}
            onClick={() => (confirmClose ? close.mutate(leaderboard.id) : setConfirmClose(true))}
            className="rounded-lg border border-gold-500/50 px-3 py-2 text-sm text-ink-900 hover:bg-gold-500/10 disabled:opacity-40"
          >
            {close.isPending
              ? 'Closing…'
              : confirmClose
                ? 'Confirm — write the final snapshot'
                : 'Close leaderboard'}
          </button>
        ) : null}

        {confirmClose && !closed ? (
          <span className="text-[11px] leading-relaxed text-ink-800/60">
            Closing writes the final snapshot. After that, only an administrator can move the
            standings, and only with a reason.
          </span>
        ) : null}
      </div>

      <ErrorNotice
        message={describeApiError(recalculate.error) ?? describeApiError(close.error)}
        className="mt-3"
      />

      {isAdmin ? <DriftNotice query={drift} /> : null}
    </section>
  );
}

/**
 * §8.1's indicator. Any difference between live and the latest snapshot means the underlying data
 * moved since the last recalculation — a merged player, a corrected payout, a deal flag.
 *
 * Rows are the players whose <b>points</b> moved; rank movement rides along as a count, because one
 * player's points changing shifts everybody below them and a list of moved ranks is most of the
 * board with the one who actually changed lost inside it.
 *
 * Silence when there is nothing to report is the feature. A board with no snapshot has no drift
 * rather than "all of it", so a new leaderboard does not open under a warning.
 */
function DriftNotice({ query }: { query: ReturnType<typeof useLeaderboardDrift> }) {
  const [open, setOpen] = useState(false);

  if (query.isLoading || query.isError || !query.data?.hasDrift) return null;

  const drift = query.data;
  const shown = drift.rows.slice(0, 8);

  return (
    <div className="mt-3 border border-chip-500/40 bg-chip-500/5 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-ink-950">
          <AlertTriangle className="size-3.5 text-chip-500" />
          Standings have drifted since the last recalculation
        </span>
        <span className="num text-[11px] text-ink-800/60">
          {drift.playersAffected} player{drift.playersAffected === 1 ? '' : 's'}
          {drift.ranksAffected > 0 ? ` · ${drift.ranksAffected} rank changes` : ''}
        </span>
      </div>

      <p className="mt-1 text-[11.5px] leading-relaxed text-ink-800/70">
        Results behind this leaderboard changed after snapshot {drift.snapshotSequenceNumber} was
        written. Recalculate to publish the corrected figures.
      </p>

      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        className="mt-2 flex items-center gap-1 text-[11px] font-medium text-ink-800/70 hover:text-ink-950"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {open ? 'Hide who moved' : 'See who moved'}
      </button>

      {open ? (
        <ul className="mt-2 space-y-1">
          {shown.map((row) => (
            <li key={row.playerId} className="flex justify-between gap-3 text-[11.5px]">
              <span className="min-w-0 truncate text-ink-950">
                {row.playerName || 'Unknown player'}
                {row.change === 'removed' ? (
                  <span className="text-ink-800/50"> · no longer scores</span>
                ) : row.change === 'added' ? (
                  <span className="text-ink-800/50"> · new to the board</span>
                ) : null}
              </span>
              {/* The sign is carried, never an absolute size: §6.2 names points that went *down*
                  after a correction as the signature of a deal rule behaving differently. */}
              <span
                className={`num shrink-0 ${row.pointsDelta < 0 ? 'text-chip-500' : 'text-felt-700'}`}
              >
                {row.snapshotPoints} → {row.livePoints} ({row.pointsDelta > 0 ? '+' : ''}
                {row.pointsDelta})
              </span>
            </li>
          ))}
          {drift.rows.length > shown.length ? (
            <li className="text-[11px] text-ink-800/50">
              and {drift.rows.length - shown.length} more.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- shared chrome

function Header({
  title,
  summary,
  right,
}: {
  title: string;
  summary?: string;
  right?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-ink-800/10 pb-1.5">
      <div className="min-w-0">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800/50">
          {title}
        </h4>
        {summary ? <p className="mt-0.5 text-[11.5px] text-ink-800/55">{summary}</p> : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}
